import React, { memo, useCallback, useMemo, useRef } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import { useAtomCallback } from 'jotai/utils'
import {
  firstCueIdAtom,
  nextHardStartCueIdAtom,
  isCueInSoftSchedulePathAtom,
  autoScrollAtom,
  rundownAtom,
  getCueAtom,
  getIndexedCueAtom,
  filteredAndSortedCuesAtom,
} from '../../store/rundown.store'
import { computeTimingFixes } from './timing/timingFixes.js'
import { formatDurationHuman, parseRundownDate } from '../../utils'
import { formatTimeOfDay } from '@rundown-studio/timeutils'
import { CueRunState } from '@rundown-studio/utils'
import { getTimestampByCueIdAtom } from '../../store/timestamps.store.js'
import type {
  TimingFix,
  TimingFixParams,
  TimingFixPlan,
  TimingFixAction,
} from './timing/timingFixTypes'
import CueItemIndex from './CueItemIndex.jsx'
import TimingIndicator from './timing/TimingIndicator.jsx'
import {
  CUE_TYPE_CUE,
  CUE_TYPE_GROUP,
  CUE_TYPE_HEADING,
} from '../../constants/cue-types.js'
import CueTiming from './timing/CueTiming.jsx'
import { RunnerState, CueType, RundownPermission } from '@rundown-studio/types'
import { CuesGroup, GroupTreeLines } from '@/features/cues-group'
import CueItemHeader from './CueItemHeader.jsx'
import CueItemTitle from './cue-item-title'
import { cn } from '@/utils'
import { runnerStateAtom, runnerAtom } from '../../store/runner.store.js'
import {
  AddRundownCuePosition,
  useAddRundownCue,
  useUpdateRundownCue,
  useDuplicateRundownCue,
  useDeleteRundownCue,
  useUpdateRundown,
  useCreateRundownRunner,
} from '@/entities/rundown'
import CueItemSettings from './CueItemSettings.jsx'
import CueSettingsMenu from '../../features/cue-context-menu/index.js'
import { useStartRunnerTimesnap, useUpdateRunner } from '@/entities/runner'
import { CueStateIndicator } from '@/features/cue-hidden-indicator'
import {
  CueAfterDropzone,
  CueSortDropzone,
  CueDropzone,
  useDroppableCue,
} from '@/features/cues-list'
import ScrollIntoView from './ScrollIntoView.jsx'

import type { AddCueCallback } from '@/features/cue-context-menu/model/callbacks'
import { type UpdateCueCallback } from '@/features/cue-context-menu/model/callbacks'
import { useCueContext } from '@/features/cue'
import { CueCellList } from '@/features/rundown/cue/CueCellList.js'
import { CueContextProvider } from '@/features/rundown/cue/CueContext.js'

import { useRundownAccess } from '@/features/permissions/lib/use-rundown-access.js'

const CueItem = () => {
  const { hasPermission } = useRundownAccess()
  const canEdit = hasPermission(RundownPermission.EDIT)
  const readonly = !canEdit

  const { cueId } = useCueContext()

  const getCue = useAtomValue(getCueAtom)
  const sortedCues = useAtomValue(filteredAndSortedCuesAtom)
  // Imperative, not useAtomValue: the atom returns a new closure on every
  // timestamp recompute (~3x/sec while live), so subscribing would re-render
  // the whole row. Only `isCuePlayed` needs it, and only on menu open.
  const readTimestamp = useAtomCallback(
    useCallback((get, _set, id: string) => get(getTimestampByCueIdAtom)(id), []),
  )
  const cue = getCue(cueId)
  const getCueMeta = useAtomValue(getIndexedCueAtom)
  const meta = getCueMeta(cueId)

  const rundown = useAtomValue(rundownAtom)
  const runner = useAtomValue(runnerAtom)

  const runnerState = useAtomValue(runnerStateAtom)
  const firstCueId = useAtomValue(firstCueIdAtom)
  const nextHardStartCueId = useAtomValue(nextHardStartCueIdAtom)
  const isCueInSoftSchedulePath = useAtomValue(isCueInSoftSchedulePathAtom) as (
    id: string,
  ) => boolean

  const { mutate: updateRundown, isPending: isUpdatingRundown } =
    useUpdateRundown()
  const { mutate: createRundownRunner, isPending: isCreatingRundownRunner } =
    useCreateRundownRunner()
  const { mutate: addRundownCue, isPending: isAddingRundownCue } =
    useAddRundownCue()
  const { mutate: updateRundownCue, isPending: isUpdatingRundownCue } =
    useUpdateRundownCue()
  const { mutate: duplicateRundownCue, isPending: isDuplicatingRundownCue } =
    useDuplicateRundownCue()
  const { mutate: deleteRundownCue, isPending: isDeletingRundownCue } =
    useDeleteRundownCue()
  const { mutate: startRunnerTimesnap, isPending: isStartingRunnerTimesnap } =
    useStartRunnerTimesnap()
  const { mutate: updateRunner, isPending: isUpdatingRunner } =
    useUpdateRunner()

  const rundownId = rundown?.id
  const timezone = rundown?.timezone
  const isNext = cue.id === runner?.nextCueId
  const currentCueHighlightColor = rundown?.settings?.currentCueHighlightColor
  const parentBackgroundColour = meta.parentId
    ? getCue(meta.parentId)?.backgroundColor
    : cue.backgroundColor
  const skipDuringShow =
    cue.settings.skipDuringShow ||
    (meta.parentId && getCue(meta.parentId)?.settings?.skipDuringShow)

  const isLoading = useMemo(
    () =>
      isUpdatingRundown ||
      isAddingRundownCue ||
      isUpdatingRundownCue ||
      isDuplicatingRundownCue ||
      isDeletingRundownCue ||
      isStartingRunnerTimesnap ||
      isUpdatingRunner ||
      isCreatingRundownRunner,
    [
      isAddingRundownCue,
      isCreatingRundownRunner,
      isDeletingRundownCue,
      isDuplicatingRundownCue,
      isStartingRunnerTimesnap,
      isUpdatingRundown,
      isUpdatingRundownCue,
      isUpdatingRunner,
    ],
  )

  const [autoScroll] = useAtom(autoScrollAtom)

  const isAutoScrollEnabled = useMemo(() => {
    if (!rundownId) return false

    return autoScroll[rundownId]
  }, [autoScroll, rundownId])

  // Ref for heading title to enable keyboard navigation focus
  const headingTitleRef = useRef<HTMLInputElement | null>(null)

  const isFirstCue = cue.id === firstCueId
  const isNextHardStart = cue.id === nextHardStartCueId

  const isTypeCue = useMemo(() => cue.type === CUE_TYPE_CUE, [cue.type])
  const isActive = useMemo(
    () => cue.id === runner?.timesnap?.cueId,
    [cue.id, runner?.timesnap?.cueId],
  )

  const cueCellBackground = useMemo(() => {
    const userBgColor =
      cue.backgroundColor === 'transparent' ? undefined : cue.backgroundColor
    // When the user sets a currentCueHighlightColor in the rundown settings, apply that colour to an active cue
    const activeColor = isActive
      ? currentCueHighlightColor || userBgColor
      : undefined

    // Add drop indicator styling
    const backgroundColor = isTypeCue
      ? activeColor || userBgColor || '#161616'
      : userBgColor || 'black'

    return backgroundColor
  }, [cue?.backgroundColor, currentCueHighlightColor, isActive, isTypeCue])

  const cellStyle = useMemo<React.CSSProperties>(() => {
    return {
      transition: 'ease 0.3s background-color',
      backgroundColor: cueCellBackground,
      borderRadius: '0.25rem',
    }
  }, [cueCellBackground])

  const cueStyle = useMemo<React.CSSProperties>(
    () => ({
      ['--cue-cell-bg-color' as string]: cueCellBackground, // Set background color as CSS variable for use in children
    }),
    [cueCellBackground],
  )

  const indexBgColor = useMemo(() => {
    if (isActive) return '#ef4444'
    if (skipDuringShow) return '#000000'
    if (cue.backgroundColor === 'transparent' && cue.type === CUE_TYPE_CUE)
      return '#161616'
    return (cue.backgroundColor as string) || '#161616'
  }, [isActive, skipDuringShow, cue.backgroundColor, cue.type])

  async function handleSetNextCue() {
    let nextCueId: string | undefined = cue.id

    if (cue.type === CUE_TYPE_GROUP) {
      nextCueId = meta?.children?.find(
        (c) => getCue(c.id)?.type === CUE_TYPE_CUE,
      )?.id
    }

    if (!nextCueId || !rundownId) return

    if (runnerState === RunnerState.PRESHOW) {
      updateRundown({
        rundownId,
        payload: {
          startCueId: nextCueId,
        },
      })
    } else {
      updateRunner({
        rundownId,
        runnerId: runner?.id as string,
        payload: {
          nextCueId,
        },
      })
    }
  }

  async function handleJumpToCue() {
    let jumpToCueId: string | undefined = cue.id

    if (cue.type === CUE_TYPE_GROUP) {
      jumpToCueId = meta?.children?.find(
        (c) => getCue(c.id)?.type === CueType.CUE,
      )?.id
    }

    if (!jumpToCueId || !rundownId) return

    if (runner) {
      return startRunnerTimesnap({
        rundownId: rundownId,
        runnerId: runner?.id as string,
        params: {
          cueId: jumpToCueId,
          resetNextCue: true,
        },
      })
    } else {
      updateRundown(
        {
          rundownId,
          payload: {
            startCueId: jumpToCueId,
          },
        },
        {
          onSuccess() {
            createRundownRunner({
              rundownId,
            })
          },
        },
      )
    }
  }

  const handleUpdateCue: UpdateCueCallback = (payload) => {
    if (!rundownId) return

    updateRundownCue({
      rundownId,
      cueId: cue.id,
      payload,
      params: {
        isFirstCue,
      },
    })
  }

  /**
   * Turn a fix's write-intent (`action`, computed purely by `computeTimingFixes`)
   * into the matching mutation call. This is the only place the fix logic touches
   * I/O; everything upstream is plain data.
   */
  const runFixAction = (action: TimingFixAction) => {
    if (!rundownId) return
    if (action.kind === 'patch-cue-start-time') {
      updateRundownCue({
        rundownId,
        cueId: action.cueId,
        // `startDatePlus` rides along: the engine re-derives a FIXED cue's date
        // from (time-of-day, day offset), so a move across midnight is silently
        // undone if only `startTime` is written.
        payload: { startTime: action.startTime, startDatePlus: action.startDatePlus },
        params: { isFirstCue: action.isFirstCue },
      })
    } else if (action.kind === 'patch-cue-duration') {
      updateRundownCue({
        rundownId,
        cueId: action.cueId,
        payload: { duration: action.duration },
        params: { isFirstCue: action.isFirstCue },
      })
    } else {
      // Fills the gap with a new cue rather than moving an existing one, so it
      // goes through the add-cue mutation instead of a patch.
      addRundownCue({
        rundownId,
        params: { [AddRundownCuePosition.BEFORE]: action.beforeCueId },
        payload: { duration: action.duration },
      })
    }
  }

  /** Recursively bind a pure plan node's `action` to an `onSelect` closure. */
  const planToFix = (plan: TimingFixPlan): TimingFix => ({
    key: plan.key,
    label: plan.label,
    detail: plan.detail,
    disabled: plan.disabled,
    hint: plan.hint,
    submenu: plan.submenu?.map(planToFix),
    onSelect: plan.action ? () => runFixAction(plan.action!) : undefined,
  })

  /**
   * Build the "Fix" dropdown for a gap/overlap on THIS cue. All decisions and
   * labels are computed purely by `computeTimingFixes` (unit-tested); this only
   * gathers the store inputs and binds each plan's `action` to a mutation.
   * Returns [] (no menu) if the data needed isn't resolvable.
   */
  const buildFixes = ({ type }: TimingFixParams): TimingFix[] => {
    if (!rundownId || !rundown || !cue?.startTime) return []
    // Normalize the rundown start (Date | Firestore Timestamp | string).
    const rundownStartTime = parseRundownDate(rundown.startTime)
    if (!rundownStartTime) return []

    // `computeTimingFixes` (client-local, in ./timing) owns ALL the timeline
    // math: it runs createTimestamps, resolves the governing hard start, and
    // plans each strategy via ./timing/backtime. We just hand it the raw cue
    // data and formatting/label helpers.
    const plans = computeTimingFixes({
      ctx: {
        cues: sortedCues,
        cueOrder: rundown.cues,
        rundownStartTime,
        timezone: timezone ?? undefined,
        runner,
      },
      anchorCueId: cueId,
      type,
      formatTime: (d: Date) =>
        formatTimeOfDay(d, { timezone, format: rundown.settings?.todDisplayFormat, seconds: 'nonzero' }),
      formatDuration: (ms: number) => formatDurationHuman(ms, { prefix: '' }),
      getCueTitle: (id: string) => getCue(id)?.title ?? '',
      // Live/ended: the runner drives the timeline, so fixes that write an
      // already-played cue can't take effect (see computeTimingFixes). PRESHOW
      // has no runner-locked cues, so leave every fix enabled there.
      isRunning: runnerState !== RunnerState.PRESHOW,
      isCuePlayed: (id: string) => {
        const state = readTimestamp(id)?.state
        return state === CueRunState.CUE_PAST || state === CueRunState.CUE_ACTIVE
      },
    })

    return plans.map(planToFix)
  }

  const handleAddCue: AddCueCallback = ({ params, payload }) => {
    if (!rundownId) return

    addRundownCue({
      rundownId,
      params,
      payload,
    })
  }

  async function handleDuplicateCue() {
    if (!rundownId) return

    return duplicateRundownCue(
      {
        rundownId,
        cueId: cue.id,
      },
      {
        onSuccess() {},
      },
    )
  }

  function handleDeleteCue() {
    if (!rundownId) return

    deleteRundownCue({
      rundownId,
      cueId: cue.id,
    })
  }

  // const cueCellsMemo = useMemo(() => {
  //   return (
  //     <CueCells
  //       cells={cells}
  //       columns={columns}
  //       hiddenColumns={hiddenColumns}
  //       cellStyle={cellStyle}
  //       readonly={readonly}
  //     />
  //   )
  // }, [cells, columns, hiddenColumns, cellStyle, readonly])

  const { setNodeRef, isOver } = useDroppableCue({
    cue,
    dropzoneType:
      cue?.type === CueType.GROUP ? CueDropzone.GROUP : CueDropzone.CUE,
  })

  return (
    <section
      role="row"
      aria-label="Cue row"
      id={cue.id}
      className={cn(
        'relative isolate px-2 w-full',
        isFirstCue && 'pt-2',
        isNextHardStart && 'scroll-m-24',
      )}
      style={cueStyle}
    >
      <CueSortDropzone
        cue={{
          id: cue.id,
          children: meta.children,
          isPartOfGroup: !!meta.parentId,
        }}
      />

      {/* Line connecting scheduled soft cues */}
      {isCueInSoftSchedulePath(cue.id) && (
        // Emulate start time block left margin: 2 + 7 + 1 + 12 + 1 units
        <div className="absolute left-23 w-28">
          <div className="absolute left-1/2 -ml-[0.5px] w-px h-12 bg-white" />
        </div>
      )}

      {/* Active cue line above cue */}
      {isActive && (
        <>
          <ScrollIntoView following={isAutoScrollEnabled} marginTop="-110px" />
          <div className="relative">
            <span
              className={[
                isFirstCue ? 'mb-4' : '',
                'mt-4 w-full h-1 block bg-[#ef4444] outline-4 outline-black',
              ].join(' ')}
            ></span>
            <span className="absolute -top-[6px] ml-8 text-xs font-bold uppercase text-[#ef4444] bg-black px-2">
              Current cue
            </span>
          </div>
        </>
      )}

      {/* Timing Indicator (e.g. "Gap of 5 minutes") */}
      {!isFirstCue && cue ? (
        <div className="min-h-3 ml-[84px] my-0.5 w-fit">
          <TimingIndicator
            cueId={cue.id}
            timezone={timezone ?? undefined}
            buildFixes={buildFixes}
            readonly={readonly}
          />
        </div>
      ) : null}

      {/* /!* Floating Bottom Bar indicating countdown to next hard start *!/ */}
      {/* {(runnerState !== RunnerState.ENDED && isNextHardStart && !isFirstCue) ? ( */}
      {/*  <FloatingCountdownButton */}
      {/*    cue={cue} */}
      {/*    timezone={timezone} */}
      {/*    // onScrollTo={scrollIntoView} */}
      {/*  /> */}
      {/* ) : null} */}

      {/* Next cue line above cue */}
      {isNext && (
        <div className="relative">
          <span
            className={[
              'mt-1 mb-4 w-full h-[2px] block bg-gray-400 outline-4 outline-black',
            ].join(' ')}
          ></span>
          <span className="absolute -top-[6px] ml-8 text-xs font-bold uppercase text-gray-300 bg-black px-2">
            Next cue
          </span>
        </div>
      )}

      <GroupTreeLines
        cueId={cue.id}
        cueType={cue.type}
        parentId={meta.parentId}
        parentBackgroundColour={parentBackgroundColour}
        lastSibling={meta.lastSibling}
        childrenCues={meta.children}
      />

      {/* Cue Item */}
      <div
        role="none"
        className="relative group flex items-stretch min-h-[84px] gap-1 w-full"
      >
        {/**
         * Controls
         *
         * This fixes the conflicts of `draggable` with `contenteditable` and <input> fields by only
         * activating the draggable attribute if the mouse if over the draghandle area.
         */}
        <div className="z-30 w-7 flex-none">
          <CueItemSettings
            cue={cue}
            readonly={readonly}
            parentId={meta.parentId}
            onUpdateCue={handleUpdateCue}
            onDeleteCue={handleDeleteCue}
            SettingsMenu={
              <CueSettingsMenu
                cue={cue}
                parentId={meta.parentId}
                onAddCue={handleAddCue}
                onUpdateCue={handleUpdateCue}
                onDuplicateCue={handleDuplicateCue}
                onDelete={handleDeleteCue}
                onJumpToCue={handleJumpToCue}
                onSetNext={handleSetNextCue}
              />
            }
          />
        </div>

        <div
          role="none"
          className="relative group rounded-sm flex items-stretch min-h-[84px] gap-1 w-full"
        >
          <div
            ref={setNodeRef}
            className={cn(
              'absolute left-0 right-0 pointer-events-none',
              // The last cue in a group also exposes an "insert after" dropzone
              // (below), so its own "drop onto group" zone gives up some room
              meta.lastSibling && meta.parentId
                ? 'h-1/3 bottom-1/3'
                : 'h-2/3 bottom-0',
            )}
          />

          {meta.lastSibling && meta.parentId && (
            <CueAfterDropzone cueId={cue.id} />
          )}

          <div
            className={cn(
              'absolute z-10 inset-0 bg-blue-500/60 rounded-sm',
              'transition-opacity ease-in-out duration-300',
              (isOver as boolean)
                ? 'opacity-100'
                : 'opacity-0 pointer-events-none',
            )}
          />

          {/* Index */}
          {[CUE_TYPE_CUE, CUE_TYPE_GROUP].includes(cue.type) && (
            <div
              className="relative w-12 flex-none flex"
              style={{
                ...cellStyle,
                backgroundColor: indexBgColor,
              }}
            >
              <CueStateIndicator
                cueType={cue.type}
                cueSettings={cue?.settings}
                className="absolute top-2 left-1/2 -translate-x-1/2"
                skipDuringShow={skipDuringShow}
              />
              <CueItemIndex
                cueId={cue.id}
                className={
                  meta.parentId
                    ? 'text-sm font-semibold'
                    : 'text-base font-semibold'
                }
                index={meta.displayIndex}
                type={cue.type}
                readonly={readonly || cue.type === CUE_TYPE_HEADING}
                loading={isLoading}
                onSetNext={handleSetNextCue}
                onJumpToCue={handleJumpToCue}
                skipDuringShow={skipDuringShow}
              />
            </div>
          )}
          {isTypeCue && (
            <CueTiming
              rundownId={rundownId}
              cueId={cue.id}
              timezone={timezone || 'UTC'}
              cellStyle={cellStyle}
              skipDuringShow={skipDuringShow}
            />
          )}
          {cue.type === CUE_TYPE_GROUP && (
            <CuesGroup
              cue={cue}
              childrenCues={meta.children ?? []}
              onUpdateCue={(title) => handleUpdateCue({ title })}
              readonly={readonly || cue.settings?.preventEdits}
              style={cellStyle}
            />
          )}
          {/* Title / Heading */}
          <div
            className={cn(
              'flex-none rounded-sm transition-all',
              cue.type === CUE_TYPE_CUE && 'w-[326px]',
              cue.type === CUE_TYPE_HEADING && 'w-full max-w-[610px]',
            )}
            style={cellStyle}
          >
            {cue.type === CUE_TYPE_CUE && (
              <CueItemHeader
                onUpdateCue={handleUpdateCue}
                rundownId={rundownId}
                readonly={readonly}
              />
            )}
            {cue.type === CUE_TYPE_HEADING && (
              <div className="flex items-center h-full w-full">
                <CueItemTitle
                  className={cn(
                    'font-semibold w-full',
                    isLoading && 'bg-animated from-gray-800 to-gray-900',
                  )}
                  inputRef={headingTitleRef}
                  value={cue.title}
                  onChange={(newTitle) => handleUpdateCue({ title: newTitle })}
                  disabled={isLoading}
                  readonly={readonly || cue.settings?.preventEdits}
                />
              </div>
            )}
          </div>
          {/* Cells */}
          {[CUE_TYPE_CUE].includes(cue.type) ? (
            <CueContextProvider cueId={cueId} cue={cue}>
              <CueCellList />
            </CueContextProvider>
          ) : null}
        </div>
      </div>
      {/* End Time */}
    </section>
  )
}

export default memo(CueItem)
